/**
 * 下次默认打开页签管理器DefaultOpenTabsManager
 * 1、所有登录/刷新场景需将【默认打开页签】展示在Navbar，且按顺序
 * 2、仅当链接来源为首页，自动进入【默认打开页签】的第一项模块首页
 * 3、当为复制链接且链接不为首页时，进入旧链接页面
 * 4、每次刷新、登录、重新登录，都需重新获取加锁数据，且全局一次；
 * 5、不限跨页签/跨浏览器
 */

import Vue from 'vue'
import { Message } from 'element-ui'
import store from 'public/store'
import bus from 'public/utils/bus'
import Router from '@/layout/router'
import { http } from 'public/utils/http'
import { filterTabs } from './tabs-utils'

// 创建Vue实例来管理响应式数据
const state = new Vue({
  data() {
    return {
      defaultOpenViews: [], // 下次默认打开的页签 （数据格式：[{tag, moduleId}]）
    }
  }
})

// http请求：使用首页菜单id请求，请求失败无需提示
async function fetchData(url, params, showMsg = false) {
  const res = await http(url, params, {
    cancel: !showMsg,
    axios: {
      headers: {
        'X-menu-id': '2820215716730110'
      },
    },
    appkey: '10124',
  })
  return res
}

class DefaultOpenTabsManager {
  constructor () {
    this.max = 10 // 最大锁定次数（不考虑多页签）
    this.isLoaded = false // 重登是否已获取过数据
    this.initEvent()
  }
  get state() {
    return state
  }

  // 获取默认打开的Tab
  get defaultOpenViews() {
    return state.defaultOpenViews
  }
  // 设置默认打开的Tab
  set defaultOpenViews(views) {
    state.defaultOpenViews = views
  }

  // 请求设置下次默认打开页签
  async featDefaultOpenTabs(views, msg) {
    const params = {
      lockTabs: views.map((v) => ({
        moduleId: v.moduleId,
        modulePath: v.tag,
      })),
    }
    try {
      const isShowMsg = !!msg
      await fetchData('system.homeTabLock.saveOrUpdate', params, isShowMsg)
      this.defaultOpenViews = views // 触发响应式更新
      isShowMsg && Message.success(msg)
    } catch (error) {
      console.error('下次默认打开设置失败！', error)
    }
  }

  // 设置或解锁默认打开的标签页
  async setDefaultOpenTabs(isUnLock, selectedTag, showMsg = true) {
    let views = []
    if (!isUnLock) {
      // 设置默认：追加后去重
      views = [
        ...new Set([
          ...this.defaultOpenViews,
          { tag: selectedTag.tag, moduleId: selectedTag.menuId },
        ]),
      ]
      if (views.length > this.max) {
        Message.warning(`添加【下次默认打开】功能，页签数量不得超过${this.max}个`)
        return
      }
    } else {
      // 解锁：删除选中的标签
      views = this.defaultOpenViews.filter((v) => v.tag !== selectedTag.tag)
    }

    const message = showMsg ? (isUnLock ? '解锁成功！' : '设置成功！') : ''
    await this.featDefaultOpenTabs(views, message)
  }

  // 关闭其他页签同时解锁页签
  async unLockOtherTabs(selectedTag) {
    const views = this.defaultOpenViews
      .filter((v) => v.tag === selectedTag.tag)
      .map((v) => ({ tag: v.tag, moduleId: v.moduleId }))

    await this.featDefaultOpenTabs(views)
  }

  // 关闭全部页签同时解锁页签
  async unLockAllTabs() {
    await this.featDefaultOpenTabs([])
  }

  // 自动跳转到第一个默认打开的页签
  autoJumpFirstDefaultOpenTab() {
    const defaultOpenViews = (this.defaultOpenViews || []).map((v) => ({
      ...v,
      // 将tag 的/转成-， 例如/ams/genericSearch 转成 ams-genericSearch
      name: v.tag.replaceAll('/', '-').replace(/^-/, ''),
      path: v.tag,
    }))

    // 加载已设置的默认页签项
    let visitedViews = store.state.tagsView.visitedViews || []
    // storage 中是否包含首页缓存
    const hasVisitedHomeView = Boolean(visitedViews.length > 1 && visitedViews.find(v => v.tag === '/'))
    const tabs = filterTabs(defaultOpenViews)
    tabs.forEach((route) => {
      // 不在sessionStorage缓存中便进行添加
      if (!visitedViews.find(v => v.tag === route.tag)) {
        store.dispatch('addVisitedViews', route)
      }
    })
    const hash = window.location.hash
    // 仅打开首页（且缓存中不包含首页）时自动跳转第一个设置默认页签，其他的路由链接则跳转自己的页签模块
    const isHomeHash = hash === '#/'
    if (!isHomeHash || !defaultOpenViews.length || (isHomeHash && hasVisitedHomeView)) return

    const firstTab = defaultOpenViews[0]
    Router.push(firstTab.tag)
  }

  checkAuth (tag) {
    const authMenus = store.getters.menus
    return !!(tag && authMenus[tag])
  }

  // 获取用户设置的默认打开页签
  async getDefaultOpenTabs() {
    try {
      // 全局仅需请求一次
      if (this.isLoaded) {
        this.isLoaded = false
        return
      }
      const res = await fetchData('system.homeTabLock.getByUser', {})
      this.isLoaded = true
      const lockTabs = res.lockTabs ? JSON.parse(res.lockTabs) : []
      const views = lockTabs.map((v) => ({
        tag: v.modulePath,
        moduleId: v.moduleId,
      })).filter(v => this.checkAuth(v.tag))

      this.defaultOpenViews = views // 触发响应式更新
      this.autoJumpFirstDefaultOpenTab()
    } catch (error) {
      console.error('获取下次默认打开的Tabs失败！', error)
      this.defaultOpenViews = []
    }
  }

  // 根据visitedViews顺序对defaultOpenViews排序
  async changeDefaultTabsOrder(tagSort) {
    const defaultOpenViews = this.defaultOpenViews || []
    const newTabs = []
    // 没有默认页签无需更新
    if (!defaultOpenViews.length) return

    tagSort.forEach((tag) => {
      const view = defaultOpenViews.find((v) => v.tag === tag)
      if (view) {
        newTabs.push(view)
      }
    })

    await this.featDefaultOpenTabs(newTabs)
  }

  reset () {
    // 账号借用、不同账号登录时不触发获取默认页签
    if (!window.__ERP_IS_AGAIN_LOGIN || window.__ERP__outsideAccount || window.__ERP__diffAccount__) return
    // 不刷新浏览器重新登陆重新触发获取默认页签
    this.isLoaded = false
    this.getDefaultOpenTabs()
  }

  // 监听全局响应事件
  initEvent () {
    bus.$on('APP_LOADED', () => this.reset())
  }
}

export default new DefaultOpenTabsManager()



// WEBPACK FOOTER //
// ./src/layout/layout/setup/default-open-tabs.js